import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  leccionesAprendidasAPI,
  proyectosAPI,
  parametrosAPI,
} from '../../utils/api';
import { usePermissions } from '../../hooks/usePermissions';
import { formatFechaLima } from '../../utils/date';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  BookOpen,
  Loader2,
  Search,
  X,
  CheckCircle2,
  Circle,
  Eye,
  BarChart3,
  Download,
  Upload,
  FileSpreadsheet,
} from 'lucide-react';

const formatDate = (s) => {
  if (!s) return '—';
  return formatFechaLima(s, { day: '2-digit', month: '2-digit', year: 'numeric' }) || s;
};

const PAGE_SIZE = 25;

// Concatena nombre del proyecto con la etapa (subdivisión) cuando aplique.
// Mantiene consistencia con el detalle read-only y el export Excel.
const formatProyectoLabel = (it) =>
  it?.etapa_nombre
    ? `${it.proyecto_nombre} · ${it.etapa_nombre}`
    : it?.proyecto_nombre || '';

const LeccionesAprendidasList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Base path actual — permite preservar el contexto de URL (corta vs.
  // calificada `/constructora/calidad/...`) al navegar a hijos.
  const basePath = location.pathname.replace(/\/$/, '');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  // Catálogos
  const [proyectos, setProyectos] = useState([]);
  const [etapasDeteccion, setEtapasDeteccion] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);

  // Filtros
  const [filterProyecto, setFilterProyecto] = useState('all');
  const [filterEtapa, setFilterEtapa] = useState('all');
  const [filterEspecialidad, setFilterEspecialidad] = useState('all');
  // 'all' | 'unread' | 'read'
  const [filterLectura, setFilterLectura] = useState(
    () => new URLSearchParams(location.search).get('estado_lectura') || 'all'
  );
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const fileInputRef = React.useRef(null);
  const [importing, setImporting] = useState(false);
  const [downloadingPlantilla, setDownloadingPlantilla] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Segregación de funciones por rol
  const { canView, canCreate, canEdit, canDelete, ready: permsReady } =
    usePermissions();
  const MENU_RUTA = '/constructora/calidad/lecciones-aprendidas';
  const canViewLA = canView(MENU_RUTA);
  const canCreateLA = canCreate(MENU_RUTA);
  const canEditLA = canEdit(MENU_RUTA);
  const canDeleteLA = canDelete(MENU_RUTA);

  // Cargar catálogos una sola vez
  useEffect(() => {
    Promise.all([
      proyectosAPI.getAll().catch(() => ({ data: [] })),
      parametrosAPI.getValores('etapa_deteccion_la').catch(() => ({ data: [] })),
      parametrosAPI.getValores('especialidad_la').catch(() => ({ data: [] })),
    ]).then(([rp, re, res]) => {
      const proyData = Array.isArray(rp.data) ? rp.data : rp.data?.items || [];
      setProyectos(proyData.filter((p) => p.activo !== false));
      setEtapasDeteccion(rp ? (Array.isArray(re.data) ? re.data : re.data?.valores || []) : []);
      setEspecialidades(Array.isArray(res.data) ? res.data : res.data?.valores || []);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await leccionesAprendidasAPI.list({
        proyecto_id: filterProyecto === 'all' ? '' : filterProyecto,
        etapa_deteccion: filterEtapa === 'all' ? '' : filterEtapa,
        especialidad: filterEspecialidad === 'all' ? '' : filterEspecialidad,
        leidas_por_mi:
          filterLectura === 'unread' ? 'false'
          : filterLectura === 'read' ? 'true'
          : '',
        q: search,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Error al cargar lecciones aprendidas');
    } finally {
      setLoading(false);
    }
  }, [filterProyecto, filterEtapa, filterEspecialidad, filterLectura, search, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterProyecto = (v) => {
    setFilterProyecto(v);
    setOffset(0);
  };
  const handleFilterEtapa = (v) => {
    setFilterEtapa(v);
    setOffset(0);
  };
  const handleFilterEspecialidad = (v) => {
    setFilterEspecialidad(v);
    setOffset(0);
  };

  const applySearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput.trim());
    setOffset(0);
  };

  const clearFilters = () => {
    setFilterProyecto('all');
    setFilterEtapa('all');
    setFilterEspecialidad('all');
    setFilterLectura('all');
    setSearch('');
    setSearchInput('');
    setOffset(0);
  };

  const handleFilterLectura = (v) => {
    setFilterLectura(v);
    setOffset(0);
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `¿Eliminar la lección ${item.codigo}? Esta acción no se puede deshacer.`
      )
    )
      return;
    try {
      await leccionesAprendidasAPI.delete(item.id);
      toast.success(`Lección ${item.codigo} eliminada`);
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownloadPlantilla = async () => {
    setDownloadingPlantilla(true);
    try {
      const r = await leccionesAprendidasAPI.downloadPlantilla();
      downloadBlob(r.data, 'plantilla_lecciones_aprendidas.xlsx');
      toast.success('Plantilla descargada');
    } catch {
      toast.error('Error al descargar plantilla');
    } finally {
      setDownloadingPlantilla(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const filtros = {
        proyecto_id: filterProyecto === 'all' ? '' : filterProyecto,
        etapa_deteccion: filterEtapa === 'all' ? '' : filterEtapa,
        especialidad: filterEspecialidad === 'all' ? '' : filterEspecialidad,
      };
      const r = await leccionesAprendidasAPI.exportXlsx(filtros);
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      downloadBlob(r.data, `lecciones_aprendidas_${ts}.xlsx`);
      toast.success('Exportación lista');
    } catch (err) {
      console.error('Error al exportar lecciones aprendidas:', err);
      // Si el backend devuelve un blob de error, intenta decodificarlo a texto
      let detalle = '';
      const blob = err?.response?.data;
      if (blob && typeof blob.text === 'function') {
        try {
          const txt = await blob.text();
          detalle = txt?.slice(0, 200) || '';
        } catch {
          /* noop */
        }
      }
      toast.error(detalle ? `Error al exportar: ${detalle}` : 'Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportError(null);
    try {
      const r = await leccionesAprendidasAPI.importXlsx(file);
      const w = r.data.warnings || [];
      if (w.length > 0) {
        toast.success(
          `${r.data.creadas} lecciones importadas · ${w.length} advertencias`,
          {
            description:
              w
                .slice(0, 3)
                .map((x) => `Fila ${x.fila}/${x.columna}: ${x.mensaje}`)
                .join(' · ') +
              (w.length > 3 ? ` … +${w.length - 3} más` : ''),
            duration: 8000,
          }
        );
      } else {
        toast.success(`${r.data.creadas} lecciones importadas correctamente`);
      }
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.fila) {
        setImportError(detail);
        toast.error(
          `Error en fila ${detail.fila} · columna ${detail.columna}: ${detail.mensaje}`
        );
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Error al importar el archivo');
      }
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // Gate de acceso al módulo
  if (!permsReady) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="la-perms-loading">
        <Loader2 className="animate-spin text-[#9d8272]" size={32} />
      </div>
    );
  }
  if (!canViewLA) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-center px-6"
        data-testid="la-no-access"
      >
        <X size={48} className="text-[#c5b8a2]" />
        <h2 className="text-xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Sin acceso
        </h2>
        <p className="text-sm text-[#5A5A5A] max-w-md">
          No tienes permisos para ver las Lecciones Aprendidas. Solicita al
          administrador del sistema la habilitación del permiso{' '}
          <span className="font-mono text-xs">ver</span> en tu rol.
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 skin-page" data-testid="lecciones-aprendidas-page">
        {/* Header */}
        <div className="skin-page-head flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <img
              src="/assets/logo-clic.png"
              alt="CLIC · Centro de Lecciones de Imagina para la Calidad"
              className="h-16 sm:h-20 w-auto"
              data-testid="lecciones-aprendidas-title"
            />
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
            <button
              type="button"
              onClick={handleDownloadPlantilla}
              disabled={downloadingPlantilla}
              className="skin-btn"
              data-testid="download-plantilla-btn"
            >
              {downloadingPlantilla ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileSpreadsheet size={14} />
              )}
              Plantilla
            </button>
            {canCreateLA && (
              <button
                type="button"
                onClick={handleImportClick}
                disabled={importing}
                className="skin-btn"
                data-testid="import-xlsx-btn"
              >
                {importing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                Importar
              </button>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="skin-btn"
              data-testid="export-xlsx-btn"
            >
              {exporting ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              Exportar
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              className="hidden"
              data-testid="import-file-input"
            />
            <button
              type="button"
              onClick={() => navigate(`${basePath}/dashboard`)}
              className="skin-btn"
              data-testid="ver-dashboard-btn"
            >
              <BarChart3 size={14} />
              Dashboard
            </button>
            {canCreateLA && (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/nuevo`)}
                className="skin-btn skin-btn-primary"
                data-testid="nueva-leccion-btn"
              >
                <Plus size={14} />
                Nueva Lección
              </button>
            )}
          </div>
        </div>

        {/* Filtros */}
        <div
          className="skin-filters"
          data-testid="lecciones-filtros"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="skin-field-label">Proyecto</label>
              <Select value={filterProyecto} onValueChange={handleFilterProyecto}>
                <SelectTrigger className="skin-select h-9" data-testid="filter-proyecto">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proyectos</SelectItem>
                  {proyectos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.codigo_proyecto ? `${p.codigo_proyecto} · ` : ''}
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="skin-field-label">Etapa de detección</label>
              <Select value={filterEtapa} onValueChange={handleFilterEtapa}>
                <SelectTrigger className="skin-select h-9" data-testid="filter-etapa">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las etapas</SelectItem>
                  {etapasDeteccion.map((v) => (
                    <SelectItem key={v.id || v.valor} value={v.valor}>
                      {v.valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="skin-field-label">Especialidad</label>
              <Select
                value={filterEspecialidad}
                onValueChange={handleFilterEspecialidad}
              >
                <SelectTrigger className="skin-select h-9" data-testid="filter-especialidad">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las especialidades</SelectItem>
                  {especialidades.map((v) => (
                    <SelectItem key={v.id || v.valor} value={v.valor}>
                      {v.valor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="skin-field-label">Búsqueda</label>
              <form onSubmit={applySearch} className="flex gap-1">
                <div className="relative flex-1">
                  <Search
                    size={14}
                    className="absolute left-2 top-2.5 skin-text-mute"
                  />
                  <Input
                    className="h-9 pl-7"
                    placeholder="Código, descripción, causa…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    data-testid="filter-search-input"
                  />
                </div>
                <button
                  type="submit"
                  className="skin-btn-search"
                  data-testid="filter-search-btn"
                >
                  Buscar
                </button>
              </form>
            </div>
          </div>

          {(filterProyecto !== 'all' ||
            filterEtapa !== 'all' ||
            filterEspecialidad !== 'all' ||
            filterLectura !== 'all' ||
            search) && (
            <div className="mt-3 flex items-center justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="filter-clear-btn"
              >
                <X size={14} className="mr-1" />
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>

        {/* Import error banner */}
        {importError && (
          <div
            className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-900 flex items-start gap-2"
            data-testid="import-error-banner"
          >
            <X size={16} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <strong>Error de importación.</strong>{' '}
              Fila <strong>{importError.fila}</strong> · Columna{' '}
              <strong>{importError.columna}</strong>: {importError.mensaje}
            </div>
            <button
              onClick={() => setImportError(null)}
              className="text-red-700 hover:text-red-900"
              data-testid="dismiss-import-error"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Tabs de lectura */}
        <div
          className="skin-tabs"
          data-testid="tabs-lectura"
        >
          {[
            { key: 'all', label: 'Todas' },
            { key: 'unread', label: 'Sin leer' },
            { key: 'read', label: 'Leídas' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleFilterLectura(tab.key)}
              data-testid={`tab-lectura-${tab.key}`}
              className={`skin-tab ${filterLectura === tab.key ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <Loader2
                size={32}
                className="animate-spin text-[#9d8272] mx-auto"
              />
              <p className="mt-3 text-sm text-[#5A5A5A]">Cargando…</p>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="skin-panel p-8 text-center">
            <BookOpen size={48} className="mx-auto mb-4" style={{ color: 'var(--skin-ink-mute)' }} />
            <p className="skin-text-soft">No hay lecciones registradas</p>
            <p className="text-xs mt-1 skin-text-mute">
              {total > 0
                ? 'Ajusta los filtros para ver resultados.'
                : 'Crea la primera lección para comenzar.'}
            </p>
            {total === 0 && canCreateLA && (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/nuevo`)}
                className="skin-btn skin-btn-primary mt-4"
              >
                <Plus size={14} />
                Nueva Lección
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div
              className="hidden md:block skin-panel"
              data-testid="lecciones-table"
            >
              <div className="skin-panel-head">
                <span>Listado</span>
                <span className="skin-count">
                  ({total} {total === 1 ? 'lección' : 'lecciones'})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="skin-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Fecha</th>
                      <th>Proyecto</th>
                      <th>Etapa</th>
                      <th>Especialidad</th>
                      <th>Descripción</th>
                      <th className="text-center">Leída</th>
                      <th className="text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={it.id}
                        onClick={() =>
                          navigate(`${basePath}/${it.id}`)
                        }
                        className="cursor-pointer"
                        data-testid={`leccion-row-${it.codigo}`}
                      >
                        <td className="skin-code whitespace-nowrap">
                          {it.codigo}
                        </td>
                        <td className="whitespace-nowrap skin-text-soft">
                          {formatDate(it.fecha_identificacion)}
                        </td>
                        <td>
                          <div
                            className="truncate max-w-[180px]"
                            title={formatProyectoLabel(it)}
                          >
                            {formatProyectoLabel(it)}
                          </div>
                        </td>
                        <td>
                          <span className="skin-badge skin-badge-stage">
                            {it.etapa_deteccion}
                          </span>
                        </td>
                        <td className="skin-text-soft text-xs">
                          {it.especialidad}
                        </td>
                        <td className="skin-text-soft">
                          <div
                            className="truncate max-w-[260px]"
                            title={it.descripcion_situacion}
                          >
                            {it.descripcion_situacion}
                          </div>
                        </td>
                        <td className="text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 justify-center">
                                {it.marcada_leida_por_mi ? (
                                  <span className="skin-badge-ok">
                                    <CheckCircle2 size={14} />
                                    {it.total_lectores}
                                  </span>
                                ) : (
                                  <>
                                    <Circle size={16} style={{ color: 'var(--skin-ink-mute)' }} />
                                    <span className="text-xs skin-text-mute">
                                      {it.total_lectores}
                                    </span>
                                  </>
                                )}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {it.marcada_leida_por_mi
                                ? `La leíste · ${it.total_lectores} en total`
                                : `${it.total_lectores} usuarios la leyeron`}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                        <td>
                          <div className="flex items-center justify-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="skin-icon-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`${basePath}/${it.id}`);
                                  }}
                                  data-testid={`ver-leccion-${it.codigo}`}
                                >
                                  <Eye size={14} />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Ver detalle</TooltipContent>
                            </Tooltip>
                            {canEditLA && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="skin-icon-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `${basePath}/${it.id}?edit=1`
                                      );
                                    }}
                                    data-testid={`editar-leccion-${it.codigo}`}
                                  >
                                    <Edit size={14} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Editar</TooltipContent>
                              </Tooltip>
                            )}
                            {canDeleteLA && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="skin-icon-btn skin-icon-btn-danger"
                                    onClick={(e) => handleDelete(e, it)}
                                    data-testid={`eliminar-leccion-${it.codigo}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Eliminar</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3" data-testid="lecciones-cards">
              {items.map((it) => (
                <div
                  key={it.id}
                  onClick={() => navigate(`${basePath}/${it.id}`)}
                  className="bg-white border border-[#E8E8E7] rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow"
                  data-testid={`leccion-card-${it.codigo}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className="text-xs font-mono bg-[#f5f1ea] border-[#e3d8c6]"
                        >
                          {it.codigo}
                        </Badge>
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-white text-[#5a4a3a]"
                        >
                          {it.etapa_deteccion}
                        </Badge>
                        {it.marcada_leida_por_mi && (
                          <CheckCircle2 size={14} className="text-green-600" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-[#2D2D2D] mt-2">
                        {formatProyectoLabel(it)}
                      </p>
                      <p className="text-xs text-[#5A5A5A] mt-1 line-clamp-2">
                        {it.descripcion_situacion}
                      </p>
                      <div className="flex items-center justify-between mt-2 text-xs text-[#888888]">
                        <span>{it.especialidad}</span>
                        <span>{formatDate(it.fecha_identificacion)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-between gap-2 text-sm"
                data-testid="lecciones-pagination"
              >
                <span className="text-[#5A5A5A]">
                  Página {currentPage} de {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="page-prev-btn"
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="page-next-btn"
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default LeccionesAprendidasList;
